import { useEffect, useState } from "react";
import { collection, getDocs, collectionGroup } from "firebase/firestore";
import { db } from "../../firebase.init";
import ClipLoader from "react-spinners/ClipLoader";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons';

import {
    Container,
    Row,
    Col,
    Card,
    Button,
    Form,
} from "react-bootstrap";
import EstimateModal from "../Reports/DoorDetails/EstimateModal";

const Estimates = () => {
    const [allReports, setAllReports] = useState([]);
    const [filteredReports, setFilteredReports] = useState([]);
    const [search, setSearch] = useState("");
    const [estimateTypeFilter, setEstimateTypeFilter] = useState("all");
    const [dateFilter, setDateFilter] = useState("all");
    const [loading, setLoading] = useState(true);
    const [loadingProgress, setLoadingProgress] = useState(0);

    const isMobile = window.innerWidth < 768;

    const [showEstimateModal, setShowEstimateModal] = useState(false);
    const [editingCustomEstimate, setEditingCustomEstimate] = useState(null);

    const fetchData = async () => {
        setLoading(true);
        setLoadingProgress(0);

        try {

            const [snapshot, customSnapshot, doorGroupSnapshot, resGroupSnapshot, estimateInfoGroupSnapshot] = await Promise.all([
                getDocs(collection(db, "Reports")),
                getDocs(collection(db, "custom-estimates")),
                getDocs(collectionGroup(db, "DoorReports")),
                getDocs(collectionGroup(db, "ResidentialReports")),
                getDocs(collectionGroup(db, "EstimateInfo")),
            ]);

            setLoadingProgress(20);

            const allBuildings = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            const buildingsById = new Map(allBuildings.map(b => [b.id, b]));

            const allEstimates = [];

            allBuildings.forEach(b => {
                if (b.estimatePDF && b.estimateTotal) {
                    allEstimates.push({
                        id: b.id,
                        buildingName: b.buildingName,
                        type: "Resi",
                        building: b,
                        estimatePDF: b.estimatePDF,
                        estimatePayee: b.estimatePayee,
                        estimateTotal: b.estimateTotal,
                        buildingLink: b.buildingLink,
                        estimateNo: b.estimateNo,
                        estimateDate: b.estimateDate,
                    });
                }
            });

            setLoadingProgress(35);

            doorGroupSnapshot.forEach(doc => {
                const buildingId = doc.ref.parent.parent.id;
                const b = buildingsById.get(buildingId);
                if (!b) return;

                const d = { id: doc.id, buildingName: b.buildingName, type: "Door", building: b, ...doc.data() };
                if (d.estimatePDF && d.estimateTotal) {
                    allEstimates.push(d);
                }
            });

            setLoadingProgress(50);

            resGroupSnapshot.forEach(doc => {
                const buildingId = doc.ref.parent.parent.id;
                const b = buildingsById.get(buildingId);
                if (!b) return;

                const d = { id: doc.id, buildingName: b.buildingName, type: "Residential", building: b, ...doc.data() };
                if (d.estimatePDF && d.estimateTotal) {
                    allEstimates.push(d);
                }
            });

            const sortedEstimates = [...allEstimates].sort((a, b) =>
                (a.buildingName || '').localeCompare(b.buildingName || '')
            );
            setAllReports(sortedEstimates);
            setFilteredReports(sortedEstimates);
            setLoadingProgress(65);

            const estimateInfoByCustomId = new Map();
            estimateInfoGroupSnapshot.forEach(doc => {
                const customId = doc.ref.parent.parent.id;
                if (!estimateInfoByCustomId.has(customId)) {
                    estimateInfoByCustomId.set(customId, doc.data());
                }
            });

            setLoadingProgress(80);

            const customEntries = customSnapshot.docs
                .map(doc => {
                    const customData = { id: doc.id, ...doc.data() };

                    if (!customData.estimatePDF || !customData.estimateTotal) {
                        return null;
                    }

                    const estimateInfoData = estimateInfoByCustomId.get(customData.id) || null;

                    return {
                        id: customData.id,
                        buildingName: customData.estimatePayee || 'Custom Estimate',
                        type: "Custom",
                        building: null,
                        estimatePDF: customData.estimatePDF,
                        estimatePayee: customData.estimatePayee,
                        estimateTotal: customData.estimateTotal,
                        estimateNo: customData.estimateNo,
                        estimateDate: customData.estimateDate,
                        buildingLink: null,
                        uid: customData.uid,
                        clientName: customData.clientName,
                        clientEmail: customData.clientEmail,
                        clientAccountEmail: customData.clientAccountEmail,
                        clientTelephone: customData.clientTelephone,
                        invoiceAddress: customData.invoiceAddress,
                        invoiceAddress2: customData.invoiceAddress2,
                        invoiceAddress3: customData.invoiceAddress3,
                        invoicePostcode: customData.invoicePostcode,
                        buildingAddress: customData.buildingAddress,
                        buildingAddress2: customData.buildingAddress2,
                        buildingAddress3: customData.buildingAddress3,
                        buildingPostcode: customData.buildingPostcode,
                        estimateInfoData: estimateInfoData,
                        payeeName: customData.payeeName,
                        notes: customData.notes,
                    };
                })
                .filter(Boolean);

            allEstimates.push(...customEntries);

            setLoadingProgress(95);

            const reportsList = allEstimates.sort((a, b) =>
                (a.buildingName || '').localeCompare(b.buildingName || '')
            );

            setAllReports(reportsList);
            setFilteredReports(reportsList);
            setLoadingProgress(100);

        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const parseEstimateDate = (dateString) => {
        if (!dateString) return null;

        const [datePart, timePart] = dateString.split(' ');
        const [year, month, day] = datePart.split('-');
        const [hours, minutes, seconds] = timePart.split(':');
        return new Date(year, month - 1, day, hours, minutes, seconds);
    };

    const isDateInRange = (dateString, range) => {
        if (!dateString) return false;

        const estimateDate = parseEstimateDate(dateString);
        if (!estimateDate) return false;

        const now = new Date();
        const diffTime = now - estimateDate;
        const diffDays = diffTime / (1000 * 60 * 60 * 24);

        switch (range) {
            case 'last-week':
                return diffDays <= 7;
            case 'last-2-weeks':
                return diffDays <= 14;
            case 'last-month':
                return diffDays <= 30;
            case 'last-3-months':
                return diffDays <= 90;
            case 'last-6-months':
                return diffDays <= 180;
            default:
                return true;
        }
    };

    useEffect(() => {
        const s = search.toLowerCase();

        let filtered = allReports;

        if (estimateTypeFilter === "custom") {
            filtered = filtered.filter(r => r.type === "Custom");
        } else if (estimateTypeFilter === "inspected") {
            filtered = filtered.filter(r => r.type !== "Custom");
        }

        if (dateFilter !== "all") {
            filtered = filtered.filter(r => isDateInRange(r.estimateDate, dateFilter));
        }

        if (s) {
            filtered = filtered.filter(r => {
                const buildingMatch = String(r.buildingName ?? '').toLowerCase().includes(s) ||
                    String(r.buildingAddress ?? '').toLowerCase().includes(s);
                const payeeMatch = String(r.estimatePayee ?? '').toLowerCase().includes(s);
                const totalMatch = String(r.estimateTotal ?? '').toLowerCase().includes(s);
                const estimateNoMatch = String(r.estimateNo ?? '').toLowerCase().includes(s);

                return buildingMatch || payeeMatch || totalMatch || estimateNoMatch;
            });
        }

        setFilteredReports(filtered);
    }, [search, estimateTypeFilter, dateFilter, allReports]);

    const handleEditCustomEstimate = (customEstimate) => {
        setEditingCustomEstimate(customEstimate);
        setShowEstimateModal(true);
    };

    if (loading) return (
        <div className="loading-container">
            <ClipLoader color="#b11111" size={60} />
            <div className="loading-text">
                Loading estimates... {Math.round(loadingProgress)}%
                <span className="cursor">|</span>
            </div>
            <div style={{
                width: '200px',
                height: '4px',
                backgroundColor: '#eee',
                marginTop: '10px',
                borderRadius: '2px'
            }}>
                <div style={{
                    width: `${loadingProgress}%`,
                    height: '100%',
                    backgroundColor: '#b11111',
                    transition: 'width 0.3s ease',
                    borderRadius: '2px'
                }} />
            </div>
        </div>
    );

    return (
        <div className="container">
            <Container fluid className="px-4 py-4">
                <div className="text-center mb-4">
                    <p className="largeHeading" style={{ fontSize: isMobile ? "20px" : "26px" }}>
                        Building Estimate Reports
                    </p>
                    <p className="text-muted small">
                        {allReports.length} estimates found
                    </p>
                </div>

                <Row className="justify-content-center mb-4">
                    <Col md={10} lg={9} className="mx-auto">
                        <div className="d-flex gap-2 flex-wrap align-items-center">

                            <div className="flex-grow-1" style={{ minWidth: '250px' }}>
                                <Form.Control
                                    placeholder="Search by Building, Payee, Total Price, or Estimate Number..."
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    onFocus={e => {
                                        e.target.style.outline = 'none';
                                        e.target.style.boxShadow = '0 0 0 0.25rem rgba(220, 53, 69, 0.25)';
                                        e.target.style.borderColor = '#dc3545';
                                    }}
                                    onBlur={e => {
                                        e.target.style.boxShadow = '';
                                        e.target.style.borderColor = '';
                                    }}
                                />
                            </div>

                            <div style={{ minWidth: '180px' }}>
                                <Form.Select
                                    value={estimateTypeFilter}
                                    onChange={e => setEstimateTypeFilter(e.target.value)}
                                    onFocus={e => {
                                        e.target.style.outline = 'none';
                                        e.target.style.boxShadow = '0 0 0 0.25rem rgba(220, 53, 69, 0.25)';
                                        e.target.style.borderColor = '#dc3545';
                                    }}
                                    onBlur={e => {
                                        e.target.style.boxShadow = '';
                                        e.target.style.borderColor = '';
                                    }}
                                >
                                    <option value="all">All Estimates</option>
                                    <option value="custom">Custom Estimates</option>
                                    <option value="inspected">Building Estimates</option>
                                </Form.Select>
                            </div>

                            <div style={{ minWidth: '160px' }}>
                                <Form.Select
                                    value={dateFilter}
                                    onChange={e => setDateFilter(e.target.value)}
                                    onFocus={e => {
                                        e.target.style.outline = 'none';
                                        e.target.style.boxShadow = '0 0 0 0.25rem rgba(220, 53, 69, 0.25)';
                                        e.target.style.borderColor = '#dc3545';
                                    }}
                                    onBlur={e => {
                                        e.target.style.boxShadow = '';
                                        e.target.style.borderColor = '';
                                    }}
                                >
                                    <option value="all">All Time</option>
                                    <option value="last-week">Last Week</option>
                                    <option value="last-2-weeks">Last 2 Weeks</option>
                                    <option value="last-month">Last Month</option>
                                    <option value="last-3-months">Last 3 Months</option>
                                    <option value="last-6-months">Last 6 Months</option>
                                </Form.Select>
                            </div>

                            {(search || estimateTypeFilter !== 'all' || dateFilter !== 'all') && (
                                <Button
                                    variant="outline-secondary"
                                    onClick={() => {
                                        setSearch('');
                                        setEstimateTypeFilter('all');
                                        setDateFilter('all');
                                    }}
                                    style={{ whiteSpace: 'nowrap' }}
                                >
                                    Clear Filters
                                </Button>
                            )}

                            <Button
                                variant="danger"
                                onClick={() => {
                                    setEditingCustomEstimate(null);
                                    setShowEstimateModal(true);
                                }}
                                style={{ whiteSpace: 'nowrap' }}
                            >
                                + Create Estimate
                            </Button>
                        </div>
                    </Col>
                </Row>

                {filteredReports.length === 0 ? (
                    <p className="text-center">No reports found.</p>
                ) : (
                    <Row className="g-4 mt-4">
                        {filteredReports.map((r, index) => (
                            <Col xs={6} md={4} lg={3} key={r.id + r.type + index}>
                                <Card className="shadow-sm h-100 border-0">

                                    {r.doorPullPhotoLink ? (
                                        <Card.Img
                                            src={r.doorPullPhotoLink}
                                            style={{ height: "260px", objectFit: "cover" }}
                                        />
                                    ) : r.building?.buildingLink ? (
                                        <Card.Img
                                            src={r.building.buildingLink}
                                            style={{ height: "260px", objectFit: "cover" }}
                                        />
                                    ) : (
                                        <div
                                            className="bg-light d-flex align-items-center justify-content-center"
                                            style={{ height: "260px" }}
                                        >
                                            No Image
                                        </div>
                                    )}

                                    <Card.Body className="text-center d-flex flex-column">
                                        <h6 className="fw-bold mb-2 text-truncate" title={r.buildingName}>
                                            {r.buildingName}
                                            {r.type === "Door" && r.doorNumber && ` - D${r.doorNumber}`}
                                            {r.type === "Residential" && r.doorNumber && ` - R${r.doorNumber}`}
                                            {r.type === "Resi" && " - Building"}
                                        </h6>
                                        <p className="mb-1">Payee: {r.estimatePayee}</p>
                                        <p className="mb-2">Total: £{r.estimateTotal}</p>

                                        {r.type === "Custom" ? (
                                            <div className="d-flex gap-2 mt-auto">
                                                <Button
                                                    variant="outline-danger"
                                                    onClick={() => handleEditCustomEstimate(r)}
                                                    className="flex-fill"
                                                >
                                                    <FontAwesomeIcon icon={faEdit} /> Edit
                                                </Button>
                                                <Button
                                                    variant="danger"
                                                    href={r.estimatePDF}
                                                    target="_blank"
                                                    className="flex-fill"
                                                >
                                                    View
                                                </Button>
                                            </div>
                                        ) : (
                                            <Button
                                                variant="danger"
                                                href={r.type === "Building" ? r.building.estimatePDF : r.estimatePDF}
                                                target="_blank"
                                                className="mt-auto w-100"
                                            >
                                                View Estimate
                                            </Button>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                )}
            </Container>

            <EstimateModal
                show={showEstimateModal}
                onHide={() => {
                    setShowEstimateModal(false);
                    setEditingCustomEstimate(null);
                }}
                doorData={null}
                doorsData={null}
                buildingData={null}
                customEstimateData={editingCustomEstimate}
                onGenerate={() => {
                    fetchData();
                }}
            />
        </div>
    );
};

export default Estimates;